import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './services/api';
import { parseKrimePathname, isGuestDeepLinkAppStateValid } from './utils/urlRouting';
import { resetPageMetaToDefault } from './utils/pageMeta';
import Krime from './components/Krime';
import LandingPage from './components/Landing/LandingPage';
import ResetPassword from './components/Auth/ResetPassword';
import NotFound from './components/NotFound/NotFound';
import RotationMessage from './components/Responsive/RotationMessage';
import './App.css';

window.supabase = supabase;

function resolveSessionForApp(session) {
  if (!session) return null;
  const recoverySentAt = session.user?.recovery_sent_at;
  const lastSignInAt = session.user?.last_sign_in_at;
  if (recoverySentAt && lastSignInAt) {
    const recoveryTime = new Date(recoverySentAt).getTime();
    const signInTime = new Date(lastSignInAt).getTime();
    if (Math.abs(signInTime - recoveryTime) < 60000) {
      return null;
    }
  }
  return session;
}

function AppContent() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingLocation, setPendingLocation] = useState(() => {
    try {
      const saved = localStorage.getItem('pendingSearchLocation');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const location = useLocation();
  const navigate = useNavigate();
  const prevSessionRef = useRef(undefined);
  const prevSessionForLogoutUrlRef = useRef(undefined);

  useEffect(() => {
    if (pendingLocation) {
      localStorage.setItem('pendingSearchLocation', JSON.stringify(pendingLocation));
    } else {
      localStorage.removeItem('pendingSearchLocation');
    }
  }, [pendingLocation]);

  const isResetPasswordPage = location.pathname === '/reset-password';

  useLayoutEffect(() => {
    if (loading) return;
    if (session) return;
    if (isResetPasswordPage) return;
    const parsedGuest = parseKrimePathname(location.pathname, location.search);
    const onGuestLanding =
      location.pathname === '/' || Boolean(parsedGuest.location);
    if (!onGuestLanding) return;
    resetPageMetaToDefault();
  }, [loading, session, isResetPasswordPage, location.pathname, location.search]);

  useEffect(() => {
    if (loading) return;
    const prev = prevSessionForLogoutUrlRef.current;
    const wasLoggedIn = prev != null;
    const loggedOut = session == null;
    if (wasLoggedIn && loggedOut && location.pathname !== '/') {
      navigate('/', { replace: true });
    }
    prevSessionForLogoutUrlRef.current = session;
  }, [session, location.pathname, navigate, loading]);

  useEffect(() => {
    if (isResetPasswordPage) return;
    if (prevSessionRef.current && !session) {
      setPendingLocation(null);
    }
    prevSessionRef.current = session ?? null;
  }, [session, isResetPasswordPage]);

  useEffect(() => {
    if (isResetPasswordPage) {
      setLoading(false);
      return;
    }

    const syncSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(resolveSessionForApp(session));
      setLoading(false);
    };

    syncSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(resolveSessionForApp(session));
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [isResetPasswordPage]);

  window.handleSuccessfulLogin = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setSession(resolveSessionForApp(session));
  };

  if (loading) return null;
  if (isResetPasswordPage) return <ResetPassword />;
  if (!session) {
    const parsedGuest = parseKrimePathname(location.pathname, location.search);
    const verifiedDeepLink = parsedGuest.location;
    if (location.pathname === '/') {
      return (
        <LandingPage
          onSearchLocationSelected={(loc) => setPendingLocation(loc)}
        />
      );
    }
    if (verifiedDeepLink && isGuestDeepLinkAppStateValid(location.pathname, location.search)) {
      return (
        <LandingPage
          onSearchLocationSelected={(loc) => setPendingLocation(loc)}
          deepLinkLocation={verifiedDeepLink}
          deepLinkSearch={location.search}
        />
      );
    }
    return <NotFound />;
  }

  return (
    <div className="App">
      <RotationMessage />
      <Routes>
        <Route
          path="/*"
          element={
            <Krime
              key={session.user.id}
              pendingLocation={pendingLocation}
              onPendingLocationConsumed={() => setPendingLocation(null)}
            />
          }
        />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;